import { format } from 'date-fns';
import React from 'react';
import { XAxisProps } from 'recharts';
import { useBreakpoints } from '../../../../hooks/useBreakpoints';
import { TEXT_COLOR_FOR_CHART_SVG } from '../../chartConsts';
import { ConfigChartTicks, TickChart } from 'modules/Analyze/helpers/chartHelpers';

type CustomizedAxisXTickProps = XAxisProps & {
  isBarChart?: boolean;
  ticksChart: TickChart[];
  ticksConfig?: ConfigChartTicks;
  qtyTicks: number;
  x: number;
  y: number;
  payload: {
    value: number;
    index: number;
    coordinate?: number;
  };
};

const calculateOffsetPercentage = (
  qtyTicks: number,
  minOffSet: number,
  maxOffSet: number,
  qtyMinTicks: number,
  qtyMaxTicks: number
) => {
  const diffOffset = maxOffSet - minOffSet;
  const diffQtyTicks = qtyMaxTicks - qtyMinTicks;

  // The slope of a line is a measure of its steepness
  const slope = diffOffset / diffQtyTicks;

  return minOffSet + slope * (qtyTicks - qtyMinTicks);
};

export const ChartHorizontalAxisTickView = (props: CustomizedAxisXTickProps) => {
  const breakpoint = useBreakpoints();
  const { x, y, payload, ticksChart, ticksConfig, isBarChart } = props;

  if (!ticksConfig) {
    return null;
  }
  const { displayMonthly, displayWeekly } = ticksConfig;
  const { index } = payload;
  if (ticksChart.length === 0) {
    return <></>;
  }
  const isTablet = breakpoint === 'tabletportrait';
  const isLastTick = index + 1 === ticksChart.length;
  const currentTick = ticksChart[index];
  const { date, showMonthLabel, hideTick } = currentTick;
  const isSecondToLast = ticksChart.length - 2 === index;

  if (hideTick) {
    return <></>;
  }

  const monthLabel = format(date, 'MMM');
  let fontSize = 18;

  if (isTablet) {
    fontSize = displayMonthly ? 12 : 14;
  }

  let textAnchor = 'middle';

  const isBarChartWeeklyEvery4 = isBarChart && ticksConfig.showLabelAfterEveryNTicks === 3 && !!displayWeekly;
  const isBarChartWeeklyEvery1 = isBarChart && ticksConfig.showLabelAfterEveryNTicks === 1 && !!displayWeekly;
  const isBarChartEvery7 = isBarChart && ticksConfig.showLabelAfterEveryNTicks === 6 && !Boolean(displayWeekly);
  const lastTickCutOff =
    isBarChart &&
    isLastTick &&
    ticksChart[index].hideTick === false &&
    (isBarChartWeeklyEvery4 || isBarChartWeeklyEvery1 || isBarChartEvery7);

  const isLastTickCutOff = ticksChart[index - 4]?.hideTick === false && isBarChartWeeklyEvery4 && isLastTick;
  if ((isLastTick && !isBarChart && !displayMonthly) || isBarChartEvery7 || lastTickCutOff) {
    textAnchor = 'end';
  }
  if (isSecondToLast && isTablet && !isBarChart) {
    textAnchor = 'start';
  }
  if (isLastTick && !isBarChart && !displayMonthly) {
    textAnchor = 'end';
  }
  const numTicks = ticksChart.length;
  if (displayMonthly) {
    if (!showMonthLabel) {
      return <></>;
    }

    const minOffset = 3.75; // this is the percentage that the translateX would be aligned if it has 12 months.
    const maxOffset = 2.3; // this is the percentage that the translateX would be aligned if it has 24 months.
    const labelOffset = calculateOffsetPercentage(numTicks, minOffset, maxOffset, 14, 26);

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          textAnchor={textAnchor}
          y={15}
          fill={TEXT_COLOR_FOR_CHART_SVG}
          fontSize={fontSize}
          style={{ transform: isBarChart ? '' : `translateX(-${labelOffset}%)` }}
          opacity={0.85}>
          {monthLabel}
        </text>
      </g>
    );
  }

  let textAnchorMonthLabel = 'start';
  const isSecondToLastCutOff = isSecondToLast && isBarChartEvery7 && ticksChart[index].hideTick === false;

  if (isLastTick || isSecondToLastCutOff) {
    if (!isBarChart || isBarChartWeeklyEvery1 || isLastTickCutOff || isSecondToLastCutOff) {
      textAnchorMonthLabel = 'end';
    } else {
      textAnchorMonthLabel = 'middle';
    }
  }
  let minOffset: number; // this is the percentage that the translateX in the min amount of ticks.
  let maxOffset: number; // this is the percentage that the translateX in the max amount of ticks.
  let qtyMinTicks: number;
  let qtyMaxTicks: number;
  if (isBarChartWeeklyEvery1) {
    minOffset = 2.75;
    maxOffset = 1.75;
    qtyMinTicks = 15;
    qtyMaxTicks = 27;
  } else if (isBarChartWeeklyEvery4) {
    minOffset = 2;
    maxOffset = 0.8;
    qtyMinTicks = 26;
    qtyMaxTicks = 53;
  } else {
    minOffset = 1.25;
    maxOffset = 1;
    qtyMinTicks = 65;
    qtyMaxTicks = 99;
  }

  const shouldTranslateLabel =
    (isBarChartWeeklyEvery4 && index !== 0) ||
    isBarChartWeeklyEvery1 ||
    (ticksConfig.showLabelAfterEveryNTicks === 6 && index !== 0 && isBarChart && !lastTickCutOff);
  const labelOffsetWeeklyView = calculateOffsetPercentage(numTicks, minOffset, maxOffset, qtyMinTicks, qtyMaxTicks);
  const isLastTickCutOffEvery4WeeksView = lastTickCutOff && ticksConfig.showLabelAfterEveryNTicks === 3;
  return (
    <g transform={`translate(${x},${y})`}>
      <g
        style={{
          transform: shouldTranslateLabel
            ? `translateX(${labelOffsetWeeklyView + (isLastTickCutOffEvery4WeeksView ? -0.5 : 0)}%)`
            : ''
        }}>
        <text
          x={0}
          y={0}
          dy={16}
          fontSize={fontSize}
          fill={TEXT_COLOR_FOR_CHART_SVG}
          textAnchor={textAnchor}
          opacity={0.85}>
          {date.getDate()}
        </text>
        {showMonthLabel && (
          <text
            style={{ transform: isLastTick || isSecondToLastCutOff ? '' : 'translateX(-0.5%)' }}
            textAnchor={textAnchorMonthLabel}
            fontSize={fontSize}
            y={35}
            fill={TEXT_COLOR_FOR_CHART_SVG}>
            {monthLabel}
          </text>
        )}
      </g>
    </g>
  );
};
